// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// backendConfig — the Convex experiment switches (#191).
//
// Mirrors the Realm->TanStack `conversion` model, but for the Mongo-vs-Convex
// axis and gated PER USER (not by a build-time flag):
//
//   Users.backendMode = "mongoOnly" (default) | "convexOnly" | "both"
//     mongoOnly  — today's path: Lambda + MongoDB + TanStack polling. UNCHANGED.
//     convexOnly — reads/writes go to Convex (live websocket); pollers off.
//     both       — run BOTH, compare results, log parity (Axiom + Convex table).
//
// Convex is only ever *available* when a deployment URL is configured. When it
// isn't (e.g. test env, or a build without the var), everyone is forced to
// mongoOnly and no websocket is opened — the production path is untouched.
//
// REACT_APP_BACKEND is an optional GLOBAL kill switch: set it to "mongo" to
// force every user to mongoOnly regardless of their per-user setting.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const CONVEX_URL = process.env.REACT_APP_CONVEX_URL || "";

// Convex is wired up (provider mounted, per-user gating live) only when a URL
// is present. No URL -> the whole experiment is dormant.
export const CONVEX_CONFIGURED = CONVEX_URL.length > 0;

// Optional global kill switch.
export const CONVEX_GLOBALLY_DISABLED =
  (process.env.REACT_APP_BACKEND || "").toLowerCase() === "mongo";

// Which mirrored dataset to read ("uat" | "prod"), matching the Mongo DB this
// build would otherwise talk to.
export const CONVEX_SOURCE =
  process.env.REACT_APP_MONGODB_DATABASE === "Warehouses" ? "prod" : "uat";

const VALID_MODES = new Set(["mongoOnly", "convexOnly", "both"]);

/**
 * Resolve the effective backend mode for a user document.
 * Defaults to "mongoOnly" (current behavior) when unset, invalid, when Convex
 * isn't configured, or when globally disabled — so the safe path is the default
 * in every ambiguous case.
 *
 * @param {object|null|undefined} userDocument the user's Mongo doc (tsUserDocument)
 * @returns {"mongoOnly"|"convexOnly"|"both"}
 */
export function resolveBackendMode(userDocument) {
  if (!CONVEX_CONFIGURED || CONVEX_GLOBALLY_DISABLED) return "mongoOnly";
  const mode = userDocument?.backendMode;
  return VALID_MODES.has(mode) ? mode : "mongoOnly";
}

export const usesConvexReads = (mode) =>
  mode === "convexOnly" || mode === "both";
export const usesConvexWrites = (mode) =>
  mode === "convexOnly" || mode === "both";
export const comparesBackends = (mode) => mode === "both";
